import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface UserPermission {
  id: number;
  name: string;
  overrideType: 'Allow' | 'Deny';
}

export interface ProfileGroupAssociation {
  ProfileId: number;
  GroupId: number;
}

export interface ProfileGroupAssociationsResponse {
  profileId: number;
  page: number;
  pageSize: number;
  data: ProfileGroupAssociation[];
  count: number;
}

/**
 * Mapeamento de GroupIds para módulos do sistema
 * Baseado na tabela fornecida pelo usuário
 */
export enum ModuleGroup {
  Commercial = 28,
  RecursosHumanos = 29,
  Veiculos = 30,
  Compras = 31,
  Financeiro = 32,
  /** Controladoria (centros de custo e funções correlatas). Cadastrar grupo no perfil com este Id/nome. */
  Controladoria = 41,
  /**
   * Solicitação de Compras (fluxo geral da empresa).
   * Cadastrar grupo no Admin com Id 42 e nome "SolicitacaoCompras" (ou vincular ao perfil).
   * Enquanto o grupo não existir, quem tem a âncora Compras continua enxergando o menu.
   */
  SolicitacaoCompras = 42,
  Documentos = 33,
  Produtos = 34,
  Estoque = 35,
  Operacoes = 36,
  B2B = 37,
  Administrativo = 38,
  OrdemServico = 39,
  Configuracoes = 40
}


@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private baseUrl = environment.microservices.profileGroup;
  private userGroupsSubject = new BehaviorSubject<number[]>([]);
  public userGroups$ = this.userGroupsSubject.asObservable();

  private userGroupNamesSubject = new BehaviorSubject<string[]>([]);
  public userGroupNames$ = this.userGroupNamesSubject.asObservable();

  private userPermissionsSubject = new BehaviorSubject<UserPermission[]>([]);
  public userPermissions$ = this.userPermissionsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadGroupsFromToken();
    this.loadPermissionsFromToken();
  }

  /**
   * Busca os grupos associados a um perfil específico
   */
  getProfileGroupAssociations(profileId: number, page: number = 1, pageSize: number = 100): Observable<ProfileGroupAssociationsResponse> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileGroupAssociationsResponse>(`${this.baseUrl}/GetProfileGroupAssociations`, { params }).pipe(
      catchError(err => {
        console.error('Erro ao buscar associações de grupos do perfil:', err);
        return of({ profileId, page, pageSize, data: [], count: 0 });
      })
    );
  }

  /**
   * Busca todos os grupos de um perfil específico (todas as páginas)
   */
  private async getAllGroupsForProfile(profileId: number): Promise<number[]> {
    const allGroups: number[] = [];
    let page = 1;
    const pageSize = 10;
    let hasMorePages = true;

    while (hasMorePages) {
      try {
        const response = await this.getProfileGroupAssociations(profileId, page, pageSize).toPromise();
        
        if (response && response.data && response.data.length > 0) {
          response.data.forEach(assoc => {
            if (!allGroups.includes(assoc.GroupId)) {
              allGroups.push(assoc.GroupId);
            }
          });
          
          // Se retornou menos registros que o pageSize, é a última página
          hasMorePages = response.data.length === pageSize;
          page++;
        } else {
          hasMorePages = false;
        }
      } catch (error) {
        console.error(`Erro ao buscar grupos do perfil ${profileId}, página ${page}:`, error);
        hasMorePages = false;
      }
    }

    return allGroups;
  }

  /**
   * Busca todos os grupos para todos os perfis do usuário (todas as páginas)
   */
  loadUserGroups(profileIds: number[]): Observable<number[]> {
    if (!profileIds || profileIds.length === 0) {
      return of([]);
    }

    return new Observable(observer => {
      const promises = profileIds.map(profileId => this.getAllGroupsForProfile(profileId));

      Promise.all(promises)
        .then(async results => {
          const allGroups = new Set<number>();
          results.forEach(groupIds => groupIds.forEach(groupId => allGroups.add(groupId)));
          let groupIds = Array.from(allGroups);

          // Se nenhum grupo foi encontrado, as associações foram perdidas (bug conhecido).
          // Auto-heal: busca todos os grupos disponíveis e re-associa no banco.
          if (groupIds.length === 0) {
            groupIds = await this.autoHealProfileGroups(profileIds);
          }

          this.setUserGroups(groupIds);
          observer.next(groupIds);
          observer.complete();
        })
        .catch(error => {
          console.error('Erro ao carregar grupos do usuário:', error);
          observer.next([]);
          observer.complete();
        });
    });
  }

  private async autoHealProfileGroups(profileIds: number[]): Promise<number[]> {
    try {
      const response = await this.http
        .get<any>(`${this.baseUrl}/GetGroups?pageSize=100`)
        .toPromise();
      const groupIds: number[] = (response?.data || []).map((g: any) => g.Id as number);

      if (groupIds.length === 0) return [];

      for (const profileId of profileIds) {
        await this.http
          .post(`${this.baseUrl}/AssociateGroupsToProfile`, {
            profileId,
            groupIds,
            updatedBy: 'auto-heal'
          })
          .toPromise()
          .catch(() => {});
      }

      return groupIds;
    } catch {
      return [];
    }
  }

  /**
   * Define os grupos do usuário e salva no localStorage
   */
  setUserGroups(groupIds: number[]): void {
    this.userGroupsSubject.next(groupIds);
    localStorage.setItem('userGroups', JSON.stringify(groupIds));
  }

  /**
   * Obtém os grupos do usuário atual
   */
  getUserGroups(): number[] {
    return this.userGroupsSubject.value;
  }

  /**
   * Lê GroupIds diretamente do JWT e atualiza o BehaviorSubject.
   * Chamado no construtor (page refresh) e após login.
   */
  loadGroupsFromToken(): void {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const payload = this.decodeJwtPayload(token);

      const rawIds: string = payload?.['GroupIds'] || '';
      if (rawIds) {
        const groupIds = rawIds.split(',').map(Number).filter(Boolean);
        if (groupIds.length > 0) {
          this.userGroupsSubject.next(groupIds);
          localStorage.setItem('userGroups', JSON.stringify(groupIds));
        }
      }

      const rawNames: string = payload?.['GroupNames'] || '';
      if (rawNames) {
        const groupNames = rawNames.split(',').map(n => n.trim()).filter(Boolean);
        if (groupNames.length > 0) {
          this.userGroupNamesSubject.next(groupNames);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar grupos do token:', error);
    }
  }

  /**
   * Verifica se o usuário tem acesso a um grupo específico.
   * Usa nomes do JWT quando disponíveis (robusto, independente de IDs do DB).
   * Fallback para IDs com mapeamento de ambiente.
   */
  hasGroup(groupId: number): boolean {
    // Verifica por nome (case-insensitive) — robusto contra diferença de IDs entre ambientes
    const groupNames = this.userGroupNamesSubject.value;
    if (groupNames.length > 0) {
      const name = ModuleGroup[groupId]; // ex: 28 → 'Commercial'
      if (name && groupNames.some(n => n.toLowerCase() === name.toLowerCase())) {
        return true;
      }
    }
    // Fallback: verifica por ID com mapeamento de ambiente
    const resolvedGroupId = this.resolveGroupIdForEnvironment(groupId);
    if (this.userGroupsSubject.value.includes(resolvedGroupId)) {
      return true;
    }

    // Migração: quem ainda só tem âncora Financeiro enxerga Controladoria (centro de custo saiu de Financeiro).
    if (groupId === ModuleGroup.Controladoria) {
      const finName = ModuleGroup[ModuleGroup.Financeiro];
      if (groupNames.length > 0 && finName && groupNames.some(n => n.toLowerCase() === finName.toLowerCase())) {
        return true;
      }
      return this.userGroupsSubject.value.includes(this.resolveGroupIdForEnvironment(ModuleGroup.Financeiro));
    }

    // Migração: Solicitação de Compras ainda não cadastrada → quem tem Compras mantém acesso.
    if (groupId === ModuleGroup.SolicitacaoCompras) {
      const comprasName = ModuleGroup[ModuleGroup.Compras];
      if (groupNames.length > 0 && comprasName && groupNames.some(n => n.toLowerCase() === comprasName.toLowerCase())) {
        return true;
      }
      return this.userGroupsSubject.value.includes(this.resolveGroupIdForEnvironment(ModuleGroup.Compras));
    }

    return false;
  }

  /**
   * Validação semântica de acesso por "âncora".
   *
   * Hoje, a "âncora" é representada pelo mesmo GroupId usado no side-menu
   * (associação Perfil -> Grupo). Este método existe para que as telas não
   * dependam da nomenclatura "group" e sim da regra de acesso.
   */
  hasAnchor(anchorGroupId: number | ModuleGroup): boolean {
    return this.hasGroup(anchorGroupId as number);
  }

  private resolveGroupIdForEnvironment(groupId: number): number {
    return groupId;
  }

  /**
   * Verifica se o usuário tem acesso a qualquer um dos grupos fornecidos
   */
  hasAnyGroup(groupIds: number[]): boolean {
    if (!groupIds || groupIds.length === 0) {
      return true; // Se não há restrição, permite acesso
    }
    return groupIds.some(groupId => this.hasGroup(groupId));
  }

  /**
   * Versão semântica (âncora) para validar "qualquer um".
   */
  hasAnyAnchor(anchorGroupIds: Array<number | ModuleGroup>): boolean {
    return this.hasAnyGroup(anchorGroupIds as number[]);
  }

  /**
   * Verifica se o usuário tem acesso a todos os grupos fornecidos
   */
  hasAllGroups(groupIds: number[]): boolean {
    if (!groupIds || groupIds.length === 0) {
      return true;
    }
    return groupIds.every(groupId => this.hasGroup(groupId));
  }

  /**
   * Versão semântica (âncora) para validar "todos".
   */
  hasAllAnchors(anchorGroupIds: Array<number | ModuleGroup>): boolean {
    return this.hasAllGroups(anchorGroupIds as number[]);
  }

  /**
   * Limpa os grupos do usuário (usado no logout)
   */
  clearUserGroups(): void {
    this.userGroupsSubject.next([]);
    this.userGroupNamesSubject.next([]);
    localStorage.removeItem('userGroups');
  }

  /**
   * Verifica se o usuário tem permissão para acessar um módulo específico
   */
  canAccessModule(moduleGroup: ModuleGroup): boolean {
    return this.hasGroup(moduleGroup);
  }

  /**
   * Alias semântico para módulos/âncoras.
   */
  canAccessAnchor(anchorGroup: number | ModuleGroup): boolean {
    return this.hasAnchor(anchorGroup);
  }

  // ========================
  // Permissions (JWT claims)
  // ========================

  /**
   * Decodifica as claims "permission" do JWT e popula o BehaviorSubject.
   * Chamado no construtor (page refresh) e pelo AuthService após login.
   * Formato da claim: "{id}:{name}:{overrideType}" ex.: "1:CreateVehicle:Allow"
   */
  loadPermissionsFromToken(): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      this.userPermissionsSubject.next([]);
      return;
    }

    try {
      const payload = this.decodeJwtPayload(token);
      if (!payload) {
        this.userPermissionsSubject.next([]);
        return;
      }

      const raw = payload['permission'];
      if (!raw) {
        this.userPermissionsSubject.next([]);
        return;
      }

      const claims: string[] = Array.isArray(raw) ? raw : [raw];
      const permissions: UserPermission[] = claims
        .map((claim: string) => {
          const parts = claim.split(':');
          if (parts.length < 3) return null;
          const overrideType = parts[2] as 'Allow' | 'Deny';
          if (overrideType !== 'Allow' && overrideType !== 'Deny') return null;
          return { id: parseInt(parts[0], 10), name: parts[1], overrideType };
        })
        .filter((p): p is UserPermission => p !== null);

      this.userPermissionsSubject.next(permissions);
    } catch (error) {
      console.error('Erro ao decodificar permissões do token:', error);
      this.userPermissionsSubject.next([]);
    }
  }

  /**
   * Limpa as permissões do usuário (chamado no logout).
   */
  clearPermissions(): void {
    this.userPermissionsSubject.next([]);
  }

  /**
   * Retorna todas as permissões do usuário atual.
   */
  getUserPermissions(): UserPermission[] {
    return this.userPermissionsSubject.value;
  }

  /**
   * Verifica se o usuário tem uma permissão específica com OverrideType = Allow.
   * Se a permissão não estiver no JWT, retorna false (deny-by-default).
   */
  hasPermission(permissionName: string): boolean {
    const match = this.userPermissionsSubject.value.find(
      p => p.name.toLowerCase() === permissionName.toLowerCase()
    );
    if (!match) return false;
    return match.overrideType === 'Allow';
  }

  /**
   * Retorna true se o usuário tiver pelo menos uma das permissões fornecidas.
   */
  hasAnyPermission(permissionNames: string[]): boolean {
    if (!permissionNames || permissionNames.length === 0) return true;
    return permissionNames.some(name => this.hasPermission(name));
  }

  /**
   * Retorna true se o usuário tiver todas as permissões fornecidas.
   */
  hasAllPermissions(permissionNames: string[]): boolean {
    if (!permissionNames || permissionNames.length === 0) return true;
    return permissionNames.every(name => this.hasPermission(name));
  }

  private decodeJwtPayload(token: string): any | null {
    try {
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) return null;
      const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  debugPermissions(): void {
  }
}
